import { Card, CardContent, Typography, Box, Skeleton, Divider } from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    AccountBalanceWallet,
    CheckCircle,
    PendingActions,
    Business
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const FinancialItem = ({ title, value, type, loading, icon: IconComponent }) => {
    let color = 'text.primary';

    if (type === 'income' || type === 'paid') {
        color = 'success.main';
    } else if (type === 'expense' || type === 'unpaid') {
        color = 'error.main';
    } else if (type === 'net') {
        color = value >= 0 ? 'success.main' : 'error.main';
    }

    return (
        <Card sx={{ minWidth: 160, flex: 1 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography color="text.secondary" variant="caption" display="block">
                    {title}
                </Typography>
                {loading ? (
                    <Skeleton variant="text" width="60%" height={32} />
                ) : (
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <IconComponent sx={{ color, fontSize: 20 }} />
                        <Typography variant="h6" color={color} fontWeight="bold">
                            {typeof value === 'number' ? value.toLocaleString() : value}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const BranchFinancialCard = ({ branch, loading, t }) => {
    return (
        <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
                {/* Branch Header */}
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Business color="primary" />
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {branch.branchName}
                        </Typography>
                        {branch.branchLocation && (
                            <Typography variant="caption" color="text.secondary">
                                {branch.branchLocation}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Financial Data */}
                <Box display="flex" flexDirection="column" gap={1.5}>
                    {/* Paid Appointments */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('financials.paidAppointments')}
                            </Typography>
                        </Box>
                        {loading ? (
                            <Skeleton variant="text" width={40} />
                        ) : (
                            <Typography variant="body1" fontWeight="bold" color="success.main">
                                {branch.paidAppointments}
                            </Typography>
                        )}
                    </Box>

                    {/* Paid Income */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <TrendingUp sx={{ color: 'success.main', fontSize: 18 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('financials.paidIncome')}
                            </Typography>
                        </Box>
                        {loading ? (
                            <Skeleton variant="text" width={60} />
                        ) : (
                            <Typography variant="body1" fontWeight="bold" color="success.main">
                                {branch.paidIncome?.toLocaleString()}
                            </Typography>
                        )}
                    </Box>

                    {/* Unpaid Appointments */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <PendingActions sx={{ color: 'warning.main', fontSize: 18 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('financials.unpaidAppointments')}
                            </Typography>
                        </Box>
                        {loading ? (
                            <Skeleton variant="text" width={40} />
                        ) : (
                            <Typography variant="body1" fontWeight="bold" color="warning.main">
                                {branch.unpaidAppointments}
                            </Typography>
                        )}
                    </Box>

                    {/* Expenses */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <TrendingDown sx={{ color: 'error.main', fontSize: 18 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('financials.expenses')}
                            </Typography>
                        </Box>
                        {loading ? (
                            <Skeleton variant="text" width={60} />
                        ) : (
                            <Typography variant="body1" fontWeight="bold" color="error.main">
                                {branch.expenses?.toLocaleString()}
                            </Typography>
                        )}
                    </Box>

                    <Divider />

                    {/* Net Income */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <AccountBalanceWallet
                                sx={{
                                    color: branch.netIncome >= 0 ? 'success.main' : 'error.main',
                                    fontSize: 18
                                }}
                            />
                            <Typography variant="body2" fontWeight="medium">
                                {t('financials.netIncome')}
                            </Typography>
                        </Box>
                        {loading ? (
                            <Skeleton variant="text" width={60} />
                        ) : (
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                color={branch.netIncome >= 0 ? 'success.main' : 'error.main'}
                            >
                                {branch.netIncome?.toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const TotalsCard = ({ totals, loading, t }) => {
    const { isSuperAdmin } = useAuth()
    if (isSuperAdmin) {
        return (
            <Card sx={{ mb: 3, backgroundColor: 'action.hover' }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        {t('financials.allBranchesTotals')}
                    </Typography>
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <FinancialItem
                            title={t('financials.paidAppointments')}
                            value={totals?.paidAppointments || 0}
                            type="paid"
                            loading={loading}
                            icon={CheckCircle}
                        />
                        <FinancialItem
                            title={t('financials.paidIncome')}
                            value={totals?.paidIncome || 0}
                            type="income"
                            loading={loading}
                            icon={TrendingUp}
                        />
                        <FinancialItem
                            title={t('financials.unpaidAppointments')}
                            value={totals?.unpaidAppointments || 0}
                            type="unpaid"
                            loading={loading}
                            icon={PendingActions}
                        />
                        <FinancialItem
                            title={t('financials.expenses')}
                            value={totals?.expenses || 0}
                            type="expense"
                            loading={loading}
                            icon={TrendingDown}
                        />
                        <FinancialItem
                            title={t('financials.netIncome')}
                            value={totals?.netIncome || 0}
                            type="net"
                            loading={loading}
                            icon={AccountBalanceWallet}
                        />
                    </Box>
                </CardContent>
            </Card>
        );
    };
}
const FinancialsCard = ({ financials, loading }) => {
    const { t } = useTranslation();

    const totals = financials?.totals;
    const branches = financials?.branches || [];

    return (
        <Box>
            {/* Period Info */}
            {financials?.period && (
                <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                        {t('financials.period')}: {' '}
                        {new Date(financials.period.startDate).toLocaleDateString()} - {' '}
                        {new Date(financials.period.endDate).toLocaleDateString()}
                    </Typography>
                </Box>
            )}

            {/* Totals Card */}
            <TotalsCard totals={totals} loading={loading} t={t} />

            {/* Branch Cards */}
            <Typography variant="h6" fontWeight="bold" mb={2}>
                {t('financials.branchBreakdown')}
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
                {branches.length > 0 ? (
                    branches.map((branch) => (
                        <BranchFinancialCard
                            key={branch.branchId}
                            branch={branch}
                            loading={loading}
                            t={t}
                        />
                    ))
                ) : (
                    // Show skeleton cards when loading
                    loading && (
                        <>
                            <BranchFinancialCard branch={{}} loading={true} t={t} />
                            <BranchFinancialCard branch={{}} loading={true} t={t} />
                        </>
                    )
                )}
            </Box>

            {/* No data message */}
            {!loading && branches.length === 0 && (
                <Typography color="text.secondary" textAlign="center" py={4}>
                    {t('financials.noData')}
                </Typography>
            )}
        </Box>
    );
};

export default FinancialsCard;